import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import { Clip } from '../../types/clip-types';
import {
  CompositeSegment,
  ClipDeletes,
  ClipEdits
} from '../../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import {
  createSegmentTransition,
  SegmentBoundaryTransition,
  SegmentTransitionType
} from '../../types/RemotionPlayerConfigTypes/Track/SegmentTransition';
import { SegmentType } from '../../types/Segment/Type';
import { ContentMetadataStoreFactoryType } from '../../stores/contentMetadataStoreFactory';
import { getDefaultSceneData } from '../../utils/sceneUtils';

const REEL_SEGMENT_BG = '#000000';

export interface ReelSegmentRange {
  start: number;
  end: number;
}

/** Scene-seeding context for upload reels. Upload reels (no sceneOverrides) get one default scene
 *  per segment at creation time; RS reels leave scenes empty and fill them via sceneOverrides. */
export interface ReelSceneParams {
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  isSplitsTranslationEnabled: boolean;
}

/** Filter edits to only those whose word falls inside the segment's [start, end] source window.
 *  BE computes edits across the whole reel envelope; each segment must carry only its own. */
export function sliceEditsToSegment(edits: ClipEdits, start: number, end: number): ClipEdits {
  return Object.entries(edits).reduce<ClipEdits>((acc, [key, edit]) => {
    if (edit.start_time >= start && edit.end_time <= end) {
      acc[key] = edit;
    }
    return acc;
  }, {});
}

/** Map `clip_segments` to ordered composite-segment skeletons. `videos`/`layoutChanges`/`playRanges`
 *  are left empty — `createCompositeFromOldComposite` re-derives them from `segment_metadata`. */
export function buildReelCompositeSegments(clip: Clip, fps: number, sceneParams?: ReelSceneParams): CompositeSegment[] {
  const ranges: ReelSegmentRange[] = clip.asset_metadata.clip_segments ?? [];
  const contentId = clip.content?.id ?? '';
  const clipEdits: ClipEdits = clip.asset_metadata.edits ?? {};
  const projectId = clip.content?.project?.id ?? '';

  // Full source length for structural trim bounds; fall back to furthest segment end.
  const sourceDuration = clip.asset_metadata.maxVideoDuration ?? ranges.reduce((max, r) => Math.max(max, r.end), 0);
  const sourceDurationFrames = sourceDuration * fps;

  let offset = 0;
  return ranges.map(({ start, end }) => {
    const durationSecs = Math.max(0, end - start);
    const durationFrames = durationSecs * fps;
    const fromFrame = offset;
    const toFrame = offset + durationFrames;
    offset = toFrame;

    // BE computes filler-word edits across the whole reel envelope; keep only this window's.
    const edits = sliceEditsToSegment(clipEdits, start, end);

    const sourceFrom = start * fps;
    const sourceTo = end * fps;

    // uuid, like every other segment in the config. Positional ids would be stable only as long as
    // `clip_segments` order and length never change, and would collide across reels cut from the
    // same content. Generated once here so the id and its scene are built from the same value.
    const id = uuidv4();

    // Structural trim deletes (source seconds). Head cancels the seek startOffset + strikes the
    // pre-window transcript; tail strikes the post-window transcript. The removed span between two
    // kept windows is double-covered by one segment's tail and the next segment's head.
    const deletes: ClipDeletes = {};
    if (start > 0) {
      deletes['0'] = { bounds: [0, start], prev_end_time: 0, next_start_time: start, deleteType: 'MANUAL' };
    }
    if (end < sourceDuration) {
      deletes[`${end}`] = {
        bounds: [end, sourceDuration],
        prev_end_time: end,
        next_start_time: sourceDuration,
        deleteType: 'MANUAL'
      };
    }

    const segment: CompositeSegment = {
      id,
      name: 'Composite',
      type: SegmentType.COMPOSITE,
      fromFrame,
      toFrame,
      scenes: [],
      details: { background: { type: 'COLOR', value: REEL_SEGMENT_BG } },
      videos: [],
      layoutChanges: [],
      playRanges: [],
      projectId,
      contentId,
      // maxBounds = full source so the segment stays expandable; config.ts restores it after createCompositeFromOldComposite overwrites it.
      maxBounds: { fromFrame: 0, toFrame: sourceDurationFrames },
      currentBounds: { fromFrame: sourceFrom, toFrame: sourceTo },
      segment_metadata: {
        start,
        end,
        duration: durationSecs,
        deletes,
        edits,
        merges: [],
        excludes: [],
        captions: [],
        // Upload reels seed one default scene per segment here; RS reels leave this empty (sceneOverrides fills them).
        // `segmentRange`, not `compositeId`: this segment is not in `player_config` yet, so an
        // id-based lookup would silently fall back to the clip-level start/end for every segment.
        scenes: sceneParams ? [getDefaultSceneData({ clip, segmentRange: { start, end }, ...sceneParams })] : [],
        fitMode: clip.asset_metadata.fit,
        captions_lang: clip.asset_metadata.original_lang,
        maxVideoDuration: clip.asset_metadata.maxVideoDuration ?? sourceDuration
      }
    };
    return segment;
  });
}

/** Build one FADE transition per internal boundary. Used by `getPlayerConfigWithSplits` for fresh
 *  reels that have no existing `player_config` to read transitions from. */
export function buildReelSegmentTransitions(segments: CompositeSegment[], fps: number): SegmentBoundaryTransition[] {
  const transitions: SegmentBoundaryTransition[] = [];
  for (let i = 0; i < segments.length - 1; i++) {
    transitions.push(
      createSegmentTransition({
        transitionType: SegmentTransitionType.FADE,
        fromSegmentId: segments[i].id,
        toSegmentId: segments[i + 1].id,
        fps
      })
    );
  }
  return transitions;
}

/** True for any `HIGHLIGHT_REEL` clip, including single-window ones. Deliberately keyed on
 *  `clip_type` alone and NOT on `clip_segments.length`: caption derivation and pause removal must
 *  run for every reel, even one that renders through the ordinary single-composite path. The
 *  `clip_segments.length > 1` check in `getPlayerConfigWithSplits` is a separate, narrower question
 *  — whether this reel needs the splits path — and the two must not be collapsed into one. */
export function isHighlightReelClip(clip: Clip): boolean {
  return clip.asset_metadata.clip_type === 'HIGHLIGHT_REEL';
}
